<template>
  <div
    class="overview-main"
    ref="overviewMainRef"
    :style="outerBoxStyle"
  >
    <div class="overview-page-container">
    <div class="overview-scale-wrapper" :style="scaleWrapperStyle">
      <div class="overview-workflow-card">
      <div
        ref="flowInnerRef"
        class="overview-flow"
        :class="{ 'overview-flow--hr-context': hoveredFlow === 'hr' }"
        :style="scaledFlowStyle"
      >
      <canvas ref="canvasRef" class="overview-canvas"></canvas>

      <div class="overview-row-dividers" aria-hidden="true">
        <div class="overview-row-divider" style="top: 218px;"></div>
        <div class="overview-row-divider" style="top: 418px;"></div>
      </div>
      
      <!-- Section Labels (desktop only) -->
      <div class="overview-section-labels">
        <div class="overview-section-label overview-section-label--documents" style="left: 80px; top: 10px;">
          <i class="bi bi-file-earmark-text"></i>
          <span>Documents</span>
        </div>
        <div class="overview-section-label overview-section-label--entry" style="left: 440px; top: 10px;">
          <i class="bi bi-box-arrow-in-right"></i>
          <span>Entry Points</span>
        </div>
        <div class="overview-section-label overview-section-label--payments" style="left: 800px; top: 10px;">
          <i class="bi bi-cash-coin"></i>
          <span>Payments</span>
        </div>
        <div class="overview-section-label overview-section-label--banking" style="left: 1160px; top: 10px;">
          <i class="bi bi-bank"></i>
          <span>Banking</span>
        </div>
      </div>

      <!-- Row 1: Sales Cycle -->
      <div class="overview-flow-group overview-flow-group--sales">
        <div
          class="overview-flow-row-label"
          style="left: 10px; top: 70px;"
          @mouseenter="setHoveredFlow('sales')"
          @mouseleave="scheduleClearHoveredFlow"
        >Sales</div>
        <div class="overview-flow-group__steps">
          <router-link
            :to="`/company/${companyId}/dashboard/customers/invoices`"
            class="overview-node overview-node--invoice"
            @mouseenter="setHoveredFlow('sales')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-file-earmark-text"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Invoices</span>
              <span class="overview-node__desc">Create and send invoices</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <router-link
            :to="`/company/${companyId}/dashboard/customers/center`"
            class="overview-node overview-node--customer"
            @mouseenter="setHoveredFlow('sales')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-person-badge"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Customers</span>
              <span class="overview-node__desc">Manage customer accounts</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <router-link
            :to="`/company/${companyId}/dashboard/customers/receive-payment`"
            class="overview-node overview-node--receive-payment"
            @mouseenter="setHoveredFlow('sales')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-cash-coin"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Receive Payment</span>
              <span class="overview-node__desc">Apply payments to invoices</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <router-link
            :to="`/company/${companyId}/dashboard/banking/reconciliations-history`"
            class="overview-node overview-node--bank-reconciliation"
            @mouseenter="setHoveredFlow('bank')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-bank"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Bank Reconciliation</span>
              <span class="overview-node__desc">Match bank transactions</span>
            </span>
          </router-link>
        </div>
      </div>

      <!-- Row 2: HR / Consultant Cycle -->
      <div class="overview-flow-group overview-flow-group--hr">
        <div
          class="overview-flow-row-label"
          style="left: 10px; top: 318px; transform: translateY(-50%);"
          @mouseenter="setHoveredFlow('hr')"
          @mouseleave="scheduleClearHoveredFlow"
        >Consultants &amp; timesheets</div>
        <div class="overview-flow-group__steps">
          <router-link
            :to="`/company/${companyId}/dashboard/consultant/timesheet`"
            class="overview-node overview-node--timesheet"
            :title="timesheetTooltip"
            :aria-label="timesheetTooltip"
            @mouseenter="setHoveredFlow('hr')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-clock-history"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Timesheets</span>
              <span class="overview-node__desc">Create first</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <router-link
            :to="`/company/${companyId}/dashboard/consultant/center`"
            class="overview-node overview-node--consultants"
            :title="consultantsTooltip"
            :aria-label="consultantsTooltip"
            @mouseenter="setHoveredFlow('hr')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-people"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Consultants</span>
              <span class="overview-node__desc">C2C · 1099 · Full-time</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <router-link
            :to="`/company/${companyId}/dashboard/banking/make-deposit`"
            class="overview-node overview-node--make-deposit"
            @mouseenter="setHoveredFlow('hr')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-cash-stack"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Make Deposit</span>
              <span class="overview-node__desc">Deposit funds to bank</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow overview-flow-arrow--to-bank" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <span class="overview-flow-arrow-label">→ Bank</span>
        </div>
      </div>

      <!-- Row 3: Purchase Cycle -->
      <div class="overview-flow-group overview-flow-group--purchases">
        <div
          class="overview-flow-row-label"
          style="left: 10px; top: 480px;"
          @mouseenter="setHoveredFlow('purchases')"
          @mouseleave="scheduleClearHoveredFlow"
        >Purchases</div>
        <div class="overview-flow-group__steps">
          <router-link
            :to="`/company/${companyId}/dashboard/vendors/create-bill`"
            class="overview-node overview-node--bill"
            @mouseenter="setHoveredFlow('purchases')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-receipt"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Bills</span>
              <span class="overview-node__desc">Record vendor bills</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <router-link
            :to="`/company/${companyId}/dashboard/vendors/center`"
            class="overview-node overview-node--vendors"
            @mouseenter="setHoveredFlow('purchases')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-building"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Vendors</span>
              <span class="overview-node__desc">Manage vendor accounts</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <router-link
            :to="`/company/${companyId}/dashboard/vendors/pay-bill`"
            class="overview-node overview-node--paybill"
            @mouseenter="setHoveredFlow('purchases')"
            @mouseleave="scheduleClearHoveredFlow"
          >
            <span class="overview-node__icon-wrap"><i class="bi bi-credit-card"></i></span>
            <span class="overview-node__text">
              <span class="overview-node__title">Pay Bill</span>
              <span class="overview-node__desc">Pay outstanding bills</span>
            </span>
          </router-link>
          <span class="overview-flow-arrow overview-flow-arrow--to-bank" aria-hidden="true"><i class="bi bi-arrow-right"></i></span>
          <span class="overview-flow-arrow-label">→ Bank</span>
        </div>
      </div>

      </div>
      </div>
    </div>

    <div class="overview-support-card">
      <div class="overview-section-title">Supporting Modules</div>
      <div class="overview-supporting-modules">
        <div class="overview-module-group overview-module-group--inventory">
          <div class="overview-module-group-title">Inventory</div>
          <router-link :to="`/company/${companyId}/dashboard/inventory/items`" class="overview-support-node overview-support-node--inventory">
            <span class="overview-support-node__icon"><i class="bi bi-box-seam"></i></span>
            <span class="overview-support-node__label">Items</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
        </div>

        <div class="overview-module-group overview-module-group--banking">
          <div class="overview-module-group-title">Banking</div>
          <router-link :to="`/company/${companyId}/dashboard/banking/accounts`" class="overview-support-node overview-support-node--banking">
            <span class="overview-support-node__icon"><i class="bi bi-wallet2"></i></span>
            <span class="overview-support-node__label">Accounts</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
          <router-link :to="`/company/${companyId}/dashboard/vendors/write-check`" class="overview-support-node overview-support-node--banking">
            <span class="overview-support-node__icon"><i class="bi bi-pen"></i></span>
            <span class="overview-support-node__label">Write Check</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
          <router-link :to="`/company/${companyId}/dashboard/banking/transfer-fund`" class="overview-support-node overview-support-node--banking">
            <span class="overview-support-node__icon"><i class="bi bi-arrow-left-right"></i></span>
            <span class="overview-support-node__label">Transfer Funds</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
        </div>

        <div class="overview-module-group overview-module-group--reports">
          <div class="overview-module-group-title">Reports</div>
          <router-link :to="`/company/${companyId}/dashboard/reports/home`" class="overview-support-node overview-support-node--reports">
            <span class="overview-support-node__icon"><i class="bi bi-graph-up"></i></span>
            <span class="overview-support-node__label">Reports</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
        </div>

        <div class="overview-module-group overview-module-group--journal">
          <div class="overview-module-group-title">Journal Entries</div>
          <router-link :to="`/company/${companyId}/dashboard/transactions?view=JournalEntries`" class="overview-support-node overview-support-node--journal">
            <span class="overview-support-node__icon"><i class="bi bi-journal-text"></i></span>
            <span class="overview-support-node__label">Journal Entries</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
        </div>

        <div class="overview-module-group overview-module-group--company">
          <div class="overview-module-group-title">Company</div>
          <router-link :to="`/company/${companyId}/dashboard/company-account-list`" class="overview-support-node overview-support-node--company">
            <span class="overview-support-node__icon"><i class="bi bi-diagram-3"></i></span>
            <span class="overview-support-node__label">Chart of Accounts</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
          <router-link :to="`/company/${companyId}/dashboard/transactions`" class="overview-support-node overview-support-node--company">
            <span class="overview-support-node__icon"><i class="bi bi-list-ul"></i></span>
            <span class="overview-support-node__label">Transactions</span>
            <i class="bi bi-chevron-right overview-support-node__chevron"></i>
          </router-link>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { useRoute } from 'vue-router'
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'

export default {
  name: 'Overview',
  setup() {
    const route = useRoute();
    const companyId = route.params.id;
    const canvasRef = ref(null);
    const overviewMainRef = ref(null);
    const flowInnerRef = ref(null);
    const flowScale = ref(1);
    const flowInnerHeight = ref(640);
    const containerWidth = ref(0);
    let animationFrame = null;
    let flowAnimRaf = null;
    let lastCanvasDpr = null;
    let resizeObserver = null;
    let flowResizeObserver = null;

    const MIN_OVERVIEW_SCALE = 0.42;
    const MAX_OVERVIEW_SCALE = 1.2;

    const FLOW_LAYOUT_WIDTH = 1400;

    /**
     * Scale from actual container width when available (ResizeObserver), else viewport.
     * Ensures diagram + Bank Reconciliation column fit without clipping.
     */
    const computeFlowScale = () => {
      let cw = containerWidth.value;
      if (cw <= 0) {
        const vw = window.innerWidth;
        const reserve = 260;
        cw = Math.max(vw - reserve, 360);
        const el = overviewMainRef.value;
        let p = el?.parentElement;
        for (let d = 0; p && d < 8; d += 1) {
          const pw = p.getBoundingClientRect().width;
          if (pw > 320 && pw <= vw + 80) {
            cw = Math.max(cw, pw - 12);
          }
          p = p.parentElement;
        }
      }
      cw = Math.max(360, cw);
      const s = cw / FLOW_LAYOUT_WIDTH;
      return Math.min(Math.max(s, MIN_OVERVIEW_SCALE), MAX_OVERVIEW_SCALE);
    };

    const measureFlowHeight = () => {
      nextTick(() => {
        if (flowInnerRef.value) {
          flowInnerHeight.value = Math.max(flowInnerRef.value.scrollHeight, 620);
        }
      });
    };

    const updateResponsiveLayout = () => {
      if (overviewMainRef.value) {
        containerWidth.value = Math.round(overviewMainRef.value.getBoundingClientRect().width) || 0;
      }
      flowScale.value = computeFlowScale();
      measureFlowHeight();
      nextTick(() => {
        drawCanvas();
      });
    };

    /** Wrapper sized to scaled dimensions so transform doesn't overflow; Bank Reconciliation stays visible */
    const scaleWrapperStyle = computed(() => {
      const s = flowScale.value;
      const h = flowInnerHeight.value;
      const w = Math.round(FLOW_LAYOUT_WIDTH * s);
      return {
        position: 'relative',
        width: `${w}px`,
        maxWidth: '100%',
        height: `${Math.ceil(h * s)}px`,
        overflow: 'hidden',
        margin: '0 auto',
        flexShrink: 0
      };
    });

    const outerBoxStyle = computed(() => ({
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      overflow: 'visible',
      minHeight: '100vh',
      boxSizing: 'border-box'
    }));

    const scaledFlowStyle = computed(() => {
      const s = flowScale.value;
      const h = flowInnerHeight.value;
      return {
        position: 'absolute',
        left: 0,
        top: 0,
        width: `${FLOW_LAYOUT_WIDTH}px`,
        height: `${h}px`,
        transform: `scale(${s})`,
        transformOrigin: 'top left',
        boxSizing: 'border-box'
      };
    });
    const cp1x = (x1, x2) => x1 + (x2 - x1) * 0.3;
    const cp2x = (x1, x2) => x1 + (x2 - x1) * 0.7;

    // Get point and tangent along path at t in [0, 1]. Tangent points toward end (t=1).
    const getPointAndTangent = (x1, y1, x2, y2, t, isVertical) => {
      if (isVertical) {
        return {
          x: x1 + (x2 - x1) * t,
          y: y1 + (y2 - y1) * t,
          angle: Math.atan2(y2 - y1, x2 - x1)
        };
      }
      const cp1 = cp1x(x1, x2);
      const cp2 = cp2x(x1, x2);
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;
      const t2 = t * t;
      const t3 = t2 * t;
      const x = mt3 * x1 + 3 * mt2 * t * cp1 + 3 * mt * t2 * cp2 + t3 * x2;
      const y = mt3 * y1 + 3 * mt2 * t * y1 + 3 * mt * t2 * y2 + t3 * y2;
      const tangentX = 3 * mt2 * (cp1 - x1) + 6 * mt * t * (cp2 - cp1) + 3 * t2 * (x2 - cp2);
      const tangentY = 6 * mt * t * (y2 - y1);
      const angle = Math.atan2(tangentY, tangentX);
      return { x, y, angle };
    };

    const hoveredFlow = ref(null);
    const consultantsTooltip = 'Step 1: Select consultant. Next: create timesheet.';
    const timesheetTooltip = 'Step 2: Create timesheet. Then create bills/invoices.';
    let hoverClearTimer = null;
    const HOVER_CLEAR_MS = 120;

    const setHoveredFlow = (flow) => {
      if (hoverClearTimer) {
        clearTimeout(hoverClearTimer);
        hoverClearTimer = null;
      }
      hoveredFlow.value = flow;
    };

    const scheduleClearHoveredFlow = () => {
      if (hoverClearTimer) clearTimeout(hoverClearTimer);
      hoverClearTimer = setTimeout(() => {
        hoveredFlow.value = null;
        hoverClearTimer = null;
      }, HOVER_CLEAR_MS);
    };

    const connectionMatchesHover = (conn, flowKey) => {
      if (!flowKey) return false;
      return conn.flows.includes(flowKey);
    };

    const FLOW_PACKET_PERIOD_MS = 4200;

    const EMPHASIZED_LINE = '#3b82f6';
    const MUTED_LINE = 'rgba(203, 213, 225, 0.9)';

    const addConnectionPath = (ctx, x1, y1, x2, y2, isVertical) => {
      ctx.beginPath();
      if (isVertical) {
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
      } else {
        const c1 = x1 + (x2 - x1) * 0.3;
        const c2 = x1 + (x2 - x1) * 0.7;
        ctx.moveTo(x1, y1);
        ctx.bezierCurveTo(c1, y1, c2, y2, x2, y2);
      }
    };

    const wrapT = (t) => {
      let u = t;
      while (u < 0) u += 1;
      while (u >= 1) u -= 1;
      return u;
    };

    /** Trail + head; drawn for each pulse in the stream */
    const PACKET_LAYERS = [
      { lag: 0.09, rx: 3.6, ry: 1.85, fill: 'rgba(147, 197, 253, 0.32)', stroke: 'rgba(37, 99, 235, 0.24)', sw: 0.8 },
      { lag: 0.048, rx: 4.8, ry: 2.5, fill: 'rgba(191, 219, 254, 0.78)', stroke: 'rgba(37, 99, 235, 0.42)', sw: 0.9 },
      { lag: 0, rx: 5, ry: 2.5, fill: '#f8fafc', stroke: '#3b82f6', sw: 1 }
    ];

    const STREAM_BULLETS_ACTIVE = 4;
    const STREAM_BULLETS_IDLE = 2;

    /**
     * Continuous stream along each edge: several pulses spaced around the loop (linear motion).
     * Idle: soft stream on every connector. Hover: stronger stream only on highlighted paths.
     */
    const drawFlowPackets = (ctx, flowKey, phase01) => {
      if (phase01 == null) return;

      const focused = flowKey != null;
      const bulletCount = focused ? STREAM_BULLETS_ACTIVE : STREAM_BULLETS_IDLE;
      const alphaScale = focused ? 1 : 0.36;

      FLOW_CONNECTIONS.forEach((conn, segIndex) => {
        if (focused && !connectionMatchesHover(conn, flowKey)) return;

        const segPhase = wrapT(phase01 + segIndex * 0.024);

        for (let b = 0; b < bulletCount; b += 1) {
          const headT = wrapT(segPhase + b / bulletCount);

          for (let i = PACKET_LAYERS.length - 1; i >= 0; i -= 1) {
            const layer = PACKET_LAYERS[i];
            const t = wrapT(headT - layer.lag);
            const { x, y, angle } = getPointAndTangent(
              conn.x1,
              conn.y1,
              conn.x2,
              conn.y2,
              t,
              conn.isVertical
            );
            ctx.save();
            ctx.globalAlpha = alphaScale;
            ctx.translate(x, y);
            ctx.rotate(angle);
            ctx.beginPath();
            ctx.ellipse(0, 0, layer.rx, layer.ry, 0, 0, Math.PI * 2);
            ctx.fillStyle = layer.fill;
            ctx.fill();
            ctx.strokeStyle = layer.stroke;
            ctx.lineWidth = layer.sw;
            ctx.stroke();
            ctx.restore();
          }
        }
      });
    };

    const drawConnection = (ctx, x1, y1, x2, y2, isVertical = false, emphasized = true) => {
      addConnectionPath(ctx, x1, y1, x2, y2, isVertical);

      const lineColor = emphasized ? EMPHASIZED_LINE : MUTED_LINE;
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = emphasized ? 2 : 1.5;
      ctx.lineCap = 'round';
      ctx.setLineDash([]);
      ctx.stroke();

      const { x: midX, y: midY } = getPointAndTangent(x1, y1, x2, y2, 0.5, isVertical);
      const nodeR = emphasized ? 5 : 4;
      ctx.beginPath();
      ctx.arc(midX, midY, nodeR, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = emphasized ? 2 : 1.5;
      ctx.stroke();

      if (!emphasized) return;

      const arrowT = 0.72;
      const { x: arrowX, y: arrowY, angle } = getPointAndTangent(x1, y1, x2, y2, arrowT, isVertical);

      ctx.save();
      ctx.translate(arrowX, arrowY);
      ctx.rotate(angle);

      const arrowLength = 12;
      const arrowWidth = 8;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-arrowLength, -arrowWidth / 2);
      ctx.lineTo(-arrowLength - 2, 0);
      ctx.lineTo(-arrowLength, arrowWidth / 2);
      ctx.closePath();
      ctx.fillStyle = EMPHASIZED_LINE;
      ctx.fill();

      ctx.restore();
    };

    const CANVAS_LOGIC_WIDTH = 1100;
    const CANVAS_LOGIC_HEIGHT = 580;

    /* Stable reference — avoids reallocating on every paint */
    /* Canvas x = node left − 70; y = node top − 15 (row centers 25 / 235 / 435) */
    const FLOW_CONNECTIONS = [
      /* Invoices → Customers (animation & arrow point right toward Entry); next leg continues Invoices → Receive Payment */
      { x1: 10, y1: 25, x2: 350, y2: 25, flows: ['sales', 'hr'] },
      { x1: 10, y1: 25, x2: 690, y2: 25, flows: ['sales', 'hr'] },
      { x1: 690, y1: 25, x2: 690, y2: 235, isVertical: true, flows: ['sales', 'hr'] },
      { x1: 690, y1: 25, x2: 1030, y2: 200, flows: ['bank', 'hr'] },
      { x1: 350, y1: 235, x2: 10, y2: 235, flows: ['hr'] },
      { x1: 690, y1: 235, x2: 1030, y2: 200, flows: ['sales', 'hr', 'bank'] },
      /* Bills → Vendors (Documents → Entry); then Bills → Pay Bill */
      { x1: 10, y1: 435, x2: 350, y2: 435, flows: ['purchases', 'hr'] },
      { x1: 10, y1: 435, x2: 690, y2: 435, flows: ['purchases', 'hr'] },
      { x1: 690, y1: 435, x2: 1030, y2: 200, flows: ['purchases', 'bank', 'hr'] },
      { x1: 10, y1: 235, x2: 10, y2: 25, isVertical: true, flows: ['hr', 'sales'] },
      { x1: 10, y1: 235, x2: 10, y2: 435, isVertical: true, flows: ['hr', 'purchases'] }
    ];

    const prefersReducedMotion = () =>
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const drawCanvas = () => {
      if (!canvasRef.value) return;

      const canvas = canvasRef.value;
      /* Fixed 1100×580 logical px = node math in CSS. Parent `zoom` scales canvas + nodes together;
         do not use offsetWidth here — it can change under zoom and double-scale lines. */
      const w = CANVAS_LOGIC_WIDTH;
      const h = CANVAS_LOGIC_HEIGHT;

      const dpr = window.devicePixelRatio || 1;
      if (lastCanvasDpr !== dpr) {
        lastCanvasDpr = dpr;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
      }

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);

      const flow = hoveredFlow.value;
      const packetPhase = !prefersReducedMotion()
        ? (performance.now() % FLOW_PACKET_PERIOD_MS) / FLOW_PACKET_PERIOD_MS
        : null;

      FLOW_CONNECTIONS.forEach(conn => {
        const emphasized = !flow || connectionMatchesHover(conn, flow);
        drawConnection(ctx, conn.x1, conn.y1, conn.x2, conn.y2, conn.isVertical, emphasized);
      });

      drawFlowPackets(ctx, flow, packetPhase);
    };

    watch(hoveredFlow, () => {
      nextTick(() => drawCanvas());
    });

    const stopFlowAnimation = () => {
      if (flowAnimRaf != null) {
        cancelAnimationFrame(flowAnimRaf);
        flowAnimRaf = null;
      }
    };

    const tickFlowAnimation = () => {
      flowAnimRaf = null;
      drawCanvas();
      if (!prefersReducedMotion()) {
        flowAnimRaf = requestAnimationFrame(tickFlowAnimation);
      }
    };

    const handleResize = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(() => {
        nextTick(() => {
          updateResponsiveLayout();
        });
      });
    };

    const handleUIModeChange = () => {
      nextTick(() => {
        updateResponsiveLayout();
      });
    };

    onMounted(() => {
      nextTick(() => {
        updateResponsiveLayout();
        window.addEventListener('resize', handleResize);
        window.addEventListener('uiModeChanged', handleUIModeChange);
        if (overviewMainRef.value && typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => handleResize());
          resizeObserver.observe(overviewMainRef.value);
        }
        if (flowInnerRef.value && typeof ResizeObserver !== 'undefined') {
          flowResizeObserver = new ResizeObserver(() => {
            if (flowInnerRef.value) {
              flowInnerHeight.value = Math.max(flowInnerRef.value.scrollHeight, 620);
            }
            nextTick(() => drawCanvas());
          });
          flowResizeObserver.observe(flowInnerRef.value);
        }
        if (!prefersReducedMotion()) {
          stopFlowAnimation();
          flowAnimRaf = requestAnimationFrame(tickFlowAnimation);
        }
      });
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('uiModeChanged', handleUIModeChange);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      stopFlowAnimation();
      if (hoverClearTimer) clearTimeout(hoverClearTimer);
      resizeObserver?.disconnect();
      flowResizeObserver?.disconnect();
    });

    return {
      companyId,
      canvasRef,
      overviewMainRef,
      flowInnerRef,
      hoveredFlow,
      consultantsTooltip,
      timesheetTooltip,
      outerBoxStyle,
      scaleWrapperStyle,
      scaledFlowStyle,
      setHoveredFlow,
      scheduleClearHoveredFlow
    };
  }
}
</script>

<style src="./Overview.css"></style>
